import React, { useMemo, useState } from 'react';
import { ExpenseRecord } from '../../types';

interface ExpensesViewProps {
  expenses: ExpenseRecord[];
  onRecordExpense: (expense: Omit<ExpenseRecord, 'id' | 'createdAt'>) => void;
  onDeleteExpense: (id: string) => void;
}

export const ExpensesView: React.FC<ExpensesViewProps> = ({ expenses, onRecordExpense, onDeleteExpense }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [category, setCategory] = useState<ExpenseRecord['category']>('supplies');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      const date = e.createdAt.split('T')[0];
      if (startDate && date < startDate) return false;
      if (endDate && date > endDate) return false;
      return true;
    });
  }, [expenses, startDate, endDate]);

  const total = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  const handleAdd = () => {
    if (!description.trim() || amount <= 0) return;
    onRecordExpense({
      description: description.trim(),
      amount,
      category,
    });
    setDescription('');
    setAmount(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-black">Expenses</h2>
          <p className="text-sm text-slate-500">Track clinic spend by category.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="px-3 py-1 rounded-lg bg-amber-50 text-amber-700 font-bold">
            Total: ${total.toFixed(2)}
          </span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              Start
              <input type="date" className="border rounded-lg px-2 py-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="flex items-center gap-1">
              End
              <input type="date" className="border rounded-lg px-2 py-1" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
            <button
              type="button"
              onClick={() => { setStartDate(''); setEndDate(''); }}
              className="px-3 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border shadow-sm p-6 space-y-4 animate-scaleIn">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            className="w-full border rounded-xl p-3 text-sm"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <select
            className="w-full border rounded-xl p-3 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value as ExpenseRecord['category'])}
          >
            <option value="supplies">Supplies</option>
            <option value="lab">Lab</option>
            <option value="rent">Rent</option>
            <option value="other">Other</option>
          </select>
          <input
            type="number"
            min={0}
            step={1}
            className="w-full border rounded-xl p-3 text-sm"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-bold hover:bg-amber-700"
            onClick={handleAdd}
          >
            Add expense
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Date</th>
                <th className="text-left px-3 py-2 font-bold">Description</th>
                <th className="text-left px-3 py-2 font-bold">Category</th>
                <th className="text-left px-3 py-2 font-bold">Amount</th>
                <th className="text-left px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-slate-400" colSpan={5}>No expenses recorded.</td>
                </tr>
              ) : (
                filteredExpenses.map(exp => (
                  <tr key={exp.id} className="border-b last:border-b-0">
                    <td className="px-3 py-2">{exp.createdAt.split('T')[0]}</td>
                    <td className="px-3 py-2">{exp.description}</td>
                    <td className="px-3 py-2 capitalize">{exp.category}</td>
                    <td className="px-3 py-2 font-bold">${exp.amount.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">
                      {confirmDeleteId === exp.id ? (
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            type="button"
                            className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700"
                            onClick={() => {
                              onDeleteExpense(exp.id);
                              setConfirmDeleteId(null);
                            }}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 rounded-lg border text-xs font-bold hover:bg-slate-50"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="px-2 py-1 rounded-lg bg-red-100 text-red-700 text-xs font-bold hover:bg-red-200"
                          onClick={() => setConfirmDeleteId(exp.id)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
